/**
 * Unified parking verdict — kept in sync with app/src/services/verdict.ts.
 * Drives the badge color and headline everywhere in the app.
 */
import { computeFit } from '../types';
import type { Parking } from '../types';
import type { Tier } from '../theme/tokens';

export type VerdictState =
  | 'fits'
  | 'likely_fits'
  | 'tight'
  | 'doesnt_fit'
  | 'unknown';

export interface ParkingVerdict {
  state: VerdictState;
  tierColor: Tier;
  badge: string;
  headline: string;
  sub: string;
  clearanceCm: number;
  sortRank: number;
}

export function parkingVerdict(parking: Parking, carHeightM: number): ParkingVerdict {
  const fit = computeFit(parking, carHeightM);
  const tier = parking.confidenceTier;
  const cm = Math.round(fit.clearanceM * 100);

  if (fit.verdict === 'unknown') {
    return {
      state: 'unknown',
      tierColor: 'unknown',
      badge: 'Verify',
      headline: 'Height unknown',
      sub: 'Be the first to verify this garage',
      clearanceCm: 0,
      sortRank: 1,
    };
  }

  if (fit.verdict === 'blocked') {
    return {
      state: 'doesnt_fit',
      tierColor: 'reported',
      badge: "Doesn't fit",
      headline: "Doesn't fit",
      sub:
        tier === 'verified'
          ? `Your car is ${Math.abs(cm)} cm too tall`
          : 'Per public records — verify if you must enter',
      clearanceCm: cm,
      sortRank: 0,
    };
  }

  if (fit.verdict === 'tight') {
    return {
      state: 'tight',
      tierColor: 'likely',
      badge: 'Tight',
      headline: 'Tight — drive carefully',
      sub:
        tier === 'verified'
          ? `${cm} cm clearance · confirmed by drivers`
          : `${cm} cm clearance · verify the posted sign`,
      clearanceCm: cm,
      sortRank: 2,
    };
  }

  if (tier === 'verified') {
    return {
      state: 'fits',
      tierColor: 'verified',
      badge: 'Fits',
      headline: 'Fits',
      sub: `${cm} cm clearance · confirmed by drivers`,
      clearanceCm: cm,
      sortRank: 4,
    };
  }

  return {
    state: 'likely_fits',
    tierColor: 'likely',
    badge: 'Likely fits',
    headline: 'Likely fits — verify before you enter',
    sub: `${cm} cm clearance · confirm the posted height at the entrance`,
    clearanceCm: cm,
    sortRank: 3,
  };
}
